// @ts-strict-ignore
import * as React from 'react';
import { AppRouteComponentMatchParams, AppComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import styles from '../styles/settingsStyles';
import Descriptions from 'antd/lib/descriptions';
import {
    displayTextForLifecycleFreezeSets,
    displayTextForDayOfTheWeek,
    displayTextForReleaseScheduleCadenceStyle,
} from '../../helpers/displayHelpers';
import { runwayColors, tagStyle } from '../styles/globalStyles';
import {
    AppStoreType,
    Automation,
    AutomationType,
    DayOfTheWeek,
    LifecycleStep,
    ReleaseScheduleCadenceStyle,
    ReleaseStepType,
    SettingType,
} from '../../types';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import {
    selectAppPlatform,
    selectAppSettingAutomations,
    selectAppStoreType,
    selectLifecycleFreezes,
    selectScheduleSettings,
    selectStepConfigDataFromStepGraphs,
} from '../../selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
    createAppSettingsAutomationsRouteWithAutomation,
    createAppSettingsRouteWithSettingType,
} from '../../helpers/routeHelpers';
import { dayjs } from '@/extendedDayjs';
import Button from 'antd/lib/button';
import {
    dateSetHasFullTargetConfigurationForStepType,
    getIsOTAPlatform,
    getTargetsConfiguredForStepTypeFromDateSet,
    isMultipleReleasesCadence,
    isAndroidPlatform,
} from '@/helpers';
import { getTimezoneDiff } from '@/timeHelpers';
import { Flex, Tooltip } from 'antd/lib';
import { InfoCircleOutlined, ThunderboltFilled, WarningFilled } from '@ant-design/icons';
import { StepGraphStep } from '@/types/workflow';

type ScheduleAutomationConfig = {
    automation: Automation | undefined;
    stepName?: string;
};

const ANDROID_APP_STORE_TYPES = new Set([
    AppStoreType.Google,
    AppStoreType.Amazon,
    AppStoreType.Huawei,
    AppStoreType.Samsung,
]);

const isAndroidReleaseAutomationConfig = ({ automation, stepName }: ScheduleAutomationConfig) => {
    if (automation?.appStoreType && ANDROID_APP_STORE_TYPES.has(automation.appStoreType)) {
        return true;
    }

    if (automation?.platform && isAndroidPlatform(automation.platform)) {
        return true;
    }

    return stepName?.toLowerCase().includes('android') ?? false;
};

const automationNameForTarget = (configs: ScheduleAutomationConfig[], targetName: LifecycleTargetName) => {
    const automationName = configs.find((config) => config.automation?.name)?.automation?.name;
    if (automationName) {
        return automationName;
    }

    switch (targetName) {
        case 'kickoff':
            return 'Kick off release';
        case 'submit':
            return 'Submit app for review';
        case 'release':
            return 'Release app';
    }
};

type LifecycleTargetName = 'kickoff' | 'submit' | 'release';

const findScheduleAutomationConfigs = (
    automations: Automation[],
    stepGraphStepsById: Record<string, StepGraphStep>,
    automationType: AutomationType,
    stepType: ReleaseStepType,
): ScheduleAutomationConfig[] => {
    const automationsOfType = automations.filter((automation) => automation.type === automationType);
    const stepsOfType = Object.values(stepGraphStepsById).filter((step) => step.archetype === stepType);

    if (stepsOfType.length === 0) {
        return automationsOfType.length > 0 ?
                automationsOfType.map((automation) => ({ automation }))
            :   [{ automation: undefined }];
    }

    return stepsOfType.map((step) => ({
        automation: automationsOfType.find((automation) => automation.stepConfigId === step.stepConfigId),
        stepName: step.stepName,
    }));
};

const ReleaseScheduleSettingsComponent: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const scheduleSettings = useSelector((state: RootState) => selectScheduleSettings(state, props.appId));
    const lifecycleFreezes = useSelector((state: RootState) => selectLifecycleFreezes(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const automations = useSelector((state: RootState) => selectAppSettingAutomations(state, props.appId)) ?? [];
    const stepGraphStepsById = useSelector((state: RootState) =>
        selectStepConfigDataFromStepGraphs(state, props.appId),
    );
    const isAppleApp = appStoreType === AppStoreType.Apple;
    const isAndroidApp = isAndroidPlatform(appPlatform);
    const isAndroidStoreApp = isAndroidApp || ANDROID_APP_STORE_TYPES.has(appStoreType);
    const isOTA = getIsOTAPlatform(appPlatform);

    const haveMultiReleaseCadence = scheduleSettings ? isMultipleReleasesCadence(scheduleSettings) : false;

    const history = useHistory();
    const { appIdParam, orgIdParam } = useParams<AppRouteComponentMatchParams>();

    const scheduleIsActive =
        scheduleSettings ? scheduleSettings.scheduleCadence !== ReleaseScheduleCadenceStyle.Never : false;
    const hasKickoffTargetConfigured =
        scheduleIsActive &&
        !!scheduleSettings?.cadenceTargetDateSets?.some((dateSet) =>
            dateSetHasFullTargetConfigurationForStepType(dateSet, LifecycleStep.Kickoff),
        );
    const hasSubmitTargetConfigured =
        scheduleIsActive &&
        !!scheduleSettings?.cadenceTargetDateSets?.some((dateSet) =>
            dateSetHasFullTargetConfigurationForStepType(dateSet, LifecycleStep.Submission),
        );
    const hasReleaseTargetConfigured =
        scheduleIsActive &&
        !!scheduleSettings?.cadenceTargetDateSets?.some((dateSet) =>
            dateSetHasFullTargetConfigurationForStepType(dateSet, LifecycleStep.Release),
        );
    const kickoffAutomationConfigs = findScheduleAutomationConfigs(
        automations,
        stepGraphStepsById,
        AutomationType.AutoKickoffRelease,
        ReleaseStepType.Kickoff,
    );
    const submitAutomationConfigs = findScheduleAutomationConfigs(
        automations,
        stepGraphStepsById,
        AutomationType.AutoSubmitRelease,
        ReleaseStepType.Submission,
    );
    const releaseAutomationConfigs = findScheduleAutomationConfigs(
        automations,
        stepGraphStepsById,
        AutomationType.AutoRelease,
        ReleaseStepType.Takeoff,
    );
    const releaseAutomationStatusConfigs = releaseAutomationConfigs.filter(
        (config) => !isAndroidReleaseAutomationConfig(config),
    );
    const androidReleaseAutomationConfigs = releaseAutomationConfigs.filter(isAndroidReleaseAutomationConfig);
    const androidManualReleaseCopyConfigs =
        androidReleaseAutomationConfigs.length > 0 ? androidReleaseAutomationConfigs
        : isAndroidStoreApp ? [{ automation: undefined }]
        : [];

    const onClickGoToReleaseSettings = () => {
        const defaultsRoute = createAppSettingsRouteWithSettingType(
            orgIdParam,
            appIdParam,
            SettingType.ScheduleCadence,
        );

        history.push(defaultsRoute);
    };

    const dateComponent = (weekday: DayOfTheWeek, hour: number, minute: number, timezone: string) => {
        const momentTime = minute ? dayjs.utc(`${hour}:${minute}`, 'HH:mm') : dayjs.utc(`${hour}`, 'HH');
        return (
            <>
                {displayTextForDayOfTheWeek(weekday)} at {momentTime.format('h:mm A')}{' '}
                {getTimezoneDiff(timezone, 'GMT')}
            </>
        );
    };

    const renderConfiguredDateInfosOfType = (stepType: LifecycleStep) => {
        if (!scheduleSettings.cadenceTargetDateSets) {
            return <Text type="secondary">Not set</Text>;
        }
        const timezone = scheduleSettings?.cadenceTimezone;

        if (haveMultiReleaseCadence) {
            const dateComponents: React.JSX.Element[] = [];

            for (const dateSet of scheduleSettings.cadenceTargetDateSets) {
                const configuredTargets = getTargetsConfiguredForStepTypeFromDateSet(dateSet, stepType);

                if (configuredTargets) {
                    dateComponents.push(
                        dateComponent(configuredTargets[0], configuredTargets[1], configuredTargets[2], timezone),
                    );
                }
            }

            if (dateComponents.length === 0) {
                return <Text type="secondary">Not set</Text>;
            } else {
                return dateComponents.map((component, i) => {
                    return (
                        <li key={i}>
                            <span>• {component}</span>
                        </li>
                    );
                });
            }
        } else if (scheduleSettings?.cadenceTargetDateSets?.length > 0) {
            const dateSet = scheduleSettings.cadenceTargetDateSets[0];
            const configuredTargets = getTargetsConfiguredForStepTypeFromDateSet(dateSet, stepType);
            if (configuredTargets) {
                return dateComponent(configuredTargets[0], configuredTargets[1], configuredTargets[2], timezone);
            } else {
                return <Text type="secondary">Not set</Text>;
            }
        } else {
            return <Text type="secondary">Not set</Text>;
        }
    };

    const automationSettingsRouteForConfigs = (configs: ScheduleAutomationConfig[]) => {
        const automationId = configs.find((config) => config.automation?.id)?.automation?.id;
        return automationId ?
                createAppSettingsAutomationsRouteWithAutomation(orgIdParam, appIdParam, automationId)
            :   createAppSettingsRouteWithSettingType(orgIdParam, appIdParam, SettingType.Automations);
    };

    const stepNamesForConfigs = (configs: ScheduleAutomationConfig[]) =>
        configs.map((config) => config.stepName ?? 'Release step');

    const tooltipBodyStyle: React.CSSProperties = {
        maxHeight: 240,
        overflowY: 'auto',
        paddingRight: 4,
    };

    const renderStepList = (stepNames: string[]) => (
        <ul style={{ margin: '6px 0 10px 18px', padding: 0 }}>
            {stepNames.map((stepName, index) => (
                <li key={`${stepName}-${index}`}>{stepName}</li>
            ))}
        </ul>
    );

    const renderSettingsLink = (configs: ScheduleAutomationConfig[], prefix: React.ReactNode) => (
        <>
            {prefix} <Link to={automationSettingsRouteForConfigs(configs)}>settings</Link>.
        </>
    );

    const renderEnabledAutomationTooltip = (configs: ScheduleAutomationConfig[], targetName: LifecycleTargetName) => {
        const automationName = automationNameForTarget(configs, targetName);

        return (
            <div style={tooltipBodyStyle}>
                <div>
                    The &apos;{automationName}&apos; automation is <b>enabled</b> for:
                </div>
                {renderStepList(stepNamesForConfigs(configs))}
                <div>{renderSettingsLink(configs, 'You can disable it, or manage options from its')}</div>
            </div>
        );
    };

    const renderWarningAutomationTooltip = ({
        disabledConfigs,
        androidManualConfigs = [],
        targetName,
        manualAction,
    }: {
        disabledConfigs: ScheduleAutomationConfig[];
        androidManualConfigs?: ScheduleAutomationConfig[];
        targetName: LifecycleTargetName;
        manualAction: string;
    }) => {
        const automationName = automationNameForTarget(disabledConfigs, targetName);

        return (
            <div style={tooltipBodyStyle}>
                {disabledConfigs.length > 0 && (
                    <>
                        <div>
                            The &apos;{automationName}&apos; automation is <b>disabled for:</b>
                        </div>
                        {renderStepList(stepNamesForConfigs(disabledConfigs))}
                        <p style={{ marginBottom: 10 }}>
                            Releases with target {targetName} dates will need to be manually {manualAction}.
                        </p>
                        <div>
                            {renderSettingsLink(disabledConfigs, 'You can enable it, or manage options from its')}
                        </div>
                    </>
                )}
                {androidManualConfigs.length > 0 && (
                    <div style={{ marginTop: disabledConfigs.length > 0 ? 12 : 0 }}>
                        {androidManualConfigs.map(({ automation, stepName }, index) => (
                            <p
                                key={automation?.id ?? stepName ?? `android-release-${index}`}
                                style={{ marginBottom: 0 }}
                            >
                                {stepName ? `For "${stepName}", ` : ''}
                                Android releases must be done manually and can not be performed by a Runway automation.
                            </p>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderAutomationStatusIcons = ({
        configs,
        targetName,
        manualAction,
        androidManualConfigs = [],
    }: {
        configs: ScheduleAutomationConfig[];
        targetName: LifecycleTargetName;
        manualAction: string;
        androidManualConfigs?: ScheduleAutomationConfig[];
    }) => {
        const enabledConfigs = configs.filter((config) => config.automation?.enabled);
        const disabledConfigs = configs.filter((config) => !config.automation?.enabled);

        if (enabledConfigs.length === 0 && disabledConfigs.length === 0 && androidManualConfigs.length === 0) {
            return null;
        }

        return (
            <Space size={10} style={{ marginLeft: 16, flexShrink: 0 }}>
                {enabledConfigs.length > 0 && (
                    <Tooltip title={renderEnabledAutomationTooltip(enabledConfigs, targetName)}>
                        <ThunderboltFilled style={{ color: runwayColors.primary }} />
                    </Tooltip>
                )}
                {(disabledConfigs.length > 0 || androidManualConfigs.length > 0) && (
                    <Tooltip
                        title={renderWarningAutomationTooltip({
                            disabledConfigs,
                            androidManualConfigs,
                            targetName,
                            manualAction,
                        })}
                    >
                        <WarningFilled style={{ color: runwayColors.statusYellow }} />
                    </Tooltip>
                )}
            </Space>
        );
    };

    const renderDateInfoWithAutomationStatus = ({
        stepType,
        configs,
        targetName,
        manualAction,
        androidManualConfigs,
    }: {
        stepType: LifecycleStep;
        configs: ScheduleAutomationConfig[];
        targetName: LifecycleTargetName;
        manualAction: string;
        androidManualConfigs?: ScheduleAutomationConfig[];
    }) => {
        return (
            <Flex align="center" justify="space-between" gap={12}>
                <div>{renderConfiguredDateInfosOfType(stepType)}</div>
                {renderAutomationStatusIcons({ configs, targetName, manualAction, androidManualConfigs })}
            </Flex>
        );
    };

    return (
        <div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Space>
                        <Text style={styles.appSettingsSectionTitleStyle}>Release schedule</Text>
                        <Tag
                            style={tagStyle}
                            color={scheduleIsActive ? runwayColors.statusGreen : runwayColors.statusYellow}
                        >
                            {scheduleIsActive ? 'Active' : 'Inactive'}
                        </Tag>
                    </Space>
                    <Button onClick={onClickGoToReleaseSettings}>Schedule settings</Button>
                </div>
                <Text type="secondary">
                    Set default target dates for kickoff, submission, and release on a regular cadence. If respective
                    release cycle automations are enabled, they will execute within 5 minutes after each target date.
                </Text>
                <Descriptions
                    bordered
                    column={1}
                    style={{ marginTop: 24, marginBottom: 12 }}
                    labelStyle={{ width: 240 }}
                >
                    <Descriptions.Item label={'Release schedule cadence'}>
                        {scheduleSettings?.scheduleCadence ?
                            displayTextForReleaseScheduleCadenceStyle(
                                scheduleSettings.scheduleCadence,
                                haveMultiReleaseCadence,
                            )
                        :   <Text type="secondary">Not enabled</Text>}
                    </Descriptions.Item>
                    {scheduleIsActive && (
                        <Descriptions.Item label="Kickoff date, time">
                            {renderDateInfoWithAutomationStatus({
                                stepType: LifecycleStep.Kickoff,
                                configs: hasKickoffTargetConfigured ? kickoffAutomationConfigs : [],
                                targetName: 'kickoff',
                                manualAction: 'kicked off',
                            })}
                        </Descriptions.Item>
                    )}
                    {scheduleIsActive && !isOTA && (
                        <Descriptions.Item label="Submit date, time">
                            {renderDateInfoWithAutomationStatus({
                                stepType: LifecycleStep.Submission,
                                configs: hasSubmitTargetConfigured ? submitAutomationConfigs : [],
                                targetName: 'submit',
                                manualAction: 'submitted',
                            })}
                        </Descriptions.Item>
                    )}
                    {scheduleIsActive && (isAppleApp || isOTA || androidManualReleaseCopyConfigs.length > 0) && (
                        <Descriptions.Item label="Release date, time">
                            {renderDateInfoWithAutomationStatus({
                                stepType: LifecycleStep.Release,
                                configs: hasReleaseTargetConfigured ? releaseAutomationStatusConfigs : [],
                                targetName: 'release',
                                manualAction: 'released',
                                androidManualConfigs: hasReleaseTargetConfigured ? androidManualReleaseCopyConfigs : [],
                            })}
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label={'Lifecycle freeze dates'}>
                        {lifecycleFreezes?.length ?
                            <Flex vertical>
                                {displayTextForLifecycleFreezeSets(lifecycleFreezes).map(
                                    ([dateRange, events], index) => (
                                        <Space key={index}>
                                            <Text>{dateRange}</Text>
                                            <Tooltip title={`Events to freeze: ${events}`}>
                                                <InfoCircleOutlined style={{ color: runwayColors.darkTextGrey }} />
                                            </Tooltip>
                                        </Space>
                                    ),
                                )}
                            </Flex>
                        :   <Text type="secondary">No lifecycle freeze dates</Text>}
                    </Descriptions.Item>
                </Descriptions>
            </div>
        </div>
    );
};

export default ReleaseScheduleSettingsComponent;
